import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { type SessionType } from "@src/modules/interface";
import constate from "constate";
import { useSelector } from "react-redux";

import { hasAnyTrueValue } from "./utils";

export interface AdminMenuPrivileges {
  accountsSection?: {
    hcps: boolean;
    workplaces: boolean;
    employees: boolean;
    requirements: boolean;
    locations: boolean;
    changeLog: boolean;
    dnrs: boolean;
  };
  shiftsSection?: {
    shiftSearch: boolean;
    shiftsVerification: boolean;
    shiftLogs: boolean;
    timeclockRequests: boolean;
    noShows: boolean;
  };
  invoiceSection?: {
    byPeriod: boolean;
    byNumber: boolean;
    postInvoiceDisputes: boolean;
    preInvoiceDisputes: boolean;
  };
  payrollSection?: {
    proofOfEarning: boolean;
    shiftPayments: boolean;
  };
  adminToolsSection?: {
    timekeepingVideos: boolean;
    shiftExplainer: boolean;
    jobsScraper: boolean;
    badgeVerificationGrading: boolean;
    ssnWorkerLookup: boolean;
    workerEvidences: boolean;
    eligibility: boolean;
  };
  staffingSection?: {
    qualifications: boolean;
    workplaceTypes: boolean;
    skillAssessments: boolean;
  };
  hiringSection?: {
    availability: boolean;
    bans: boolean;
    interviews: boolean;
    scraping: boolean;
  };
  // Route authorization flags (not for menu visibility)
  routeAuth?: {
    isAdmin: boolean;
    hasBillingOrAdministrativeRole: boolean;
  };
}

/* eslint-disable complexity */
function useGetAdminMenuPrivileges(): AdminMenuPrivileges {
  const session = useSelector((state: SessionType) => state.session);
  const { admin: isAdmin, user, adminProfile } = session || {};

  const { roles } = adminProfile ?? {};

  const isSkillsAssessmentsEnabled = useCbhFlag(CbhFeatureFlag.SKILL_ASSESSMENTS_GRADERS, {
    defaultValue: { allowedUsers: [] },
  });

  const docsV2ToolsFlag = useCbhFlag(CbhFeatureFlag.DOCS_V2_TOOLS_IN_ADMIN_UI, {
    defaultValue: { isEnabled: false },
  });

  const { enabled: isPlacementScrapingRunEnabled } = useCbhFlag(
    CbhFeatureFlag.PLACEMENT_SCRAPING_RUN,
    { defaultValue: { enabled: false, dryRunEnabled: true } }
  );

  const { enabled: isPlacementScrapingConfigEnabled } = useCbhFlag(
    CbhFeatureFlag.PLACEMENT_SCRAPING_CONFIG,
    { defaultValue: { enabled: false } }
  );

  const hasBillingOrAdministrativeRole =
    (roles?.includes(EMPLOYEE_ROLES.BILLING) ?? false) ||
    (roles?.includes(EMPLOYEE_ROLES.ADMINISTRATIVE) ?? false);

  const routeAuth = {
    isAdmin: Boolean(isAdmin),
    hasBillingOrAdministrativeRole,
  };

  if (!isAdmin) {
    return { routeAuth };
  }
  const hasAdministrativeRole = roles?.includes(EMPLOYEE_ROLES.ADMINISTRATIVE) ?? false;
  const hasBillingRole = roles?.includes(EMPLOYEE_ROLES.BILLING) ?? false;
  const hasDocumentsRole = roles?.includes(EMPLOYEE_ROLES.DOCUMENTS) ?? false;
  const hasDocumentsCreationRole = roles?.includes(EMPLOYEE_ROLES.DOCUMENT_CREATION) ?? false;
  const hasInvoiceEditRole = roles?.includes(EMPLOYEE_ROLES.INVOICE_EDIT) ?? false;
  const hasManageQualificationsRole =
    roles?.includes(EMPLOYEE_ROLES.MANAGE_QUALIFICATIONS) ?? false;
  const hasManageWorkplaceTypesRole =
    roles?.includes(EMPLOYEE_ROLES.MANAGE_WORKPLACE_TYPES) ?? false;
  const hasNfcRequestRole = roles?.includes(EMPLOYEE_ROLES.NFC_REQUESTS) ?? false;
  const hasShiftPricingRole = roles?.includes(EMPLOYEE_ROLES.SHIFT_PRICING) ?? false;

  const accountsSection = {
    hcps: true,
    workplaces: true,
    employees: hasAdministrativeRole,
    requirements: hasDocumentsRole || hasDocumentsCreationRole,
    locations: true,
    changeLog: true,
    dnrs: true,
  };

  const shiftsSection = {
    shiftSearch: true,
    shiftsVerification: true,
    shiftLogs: true,
    timeclockRequests: hasAdministrativeRole || hasNfcRequestRole,
    noShows: true,
  };

  const invoiceSection = {
    byPeriod: hasAdministrativeRole || hasInvoiceEditRole,
    byNumber: hasAdministrativeRole || hasInvoiceEditRole || hasBillingRole,
    postInvoiceDisputes: hasAdministrativeRole || hasInvoiceEditRole,
    preInvoiceDisputes: hasAdministrativeRole || hasInvoiceEditRole,
  };

  const payrollSection = {
    proofOfEarning: true,
    shiftPayments: hasShiftPricingRole,
  };

  const adminToolsSection = {
    timekeepingVideos: true,
    shiftExplainer: true,
    jobsScraper: isPlacementScrapingRunEnabled,
    badgeVerificationGrading: true,
    ssnWorkerLookup: roles?.includes(EMPLOYEE_ROLES.VIEW_FULL_SSN) ?? false,
    workerEvidences: docsV2ToolsFlag.isEnabled,
    eligibility: docsV2ToolsFlag.isEnabled,
  };

  const staffingSection = {
    qualifications: hasManageQualificationsRole,
    workplaceTypes: hasManageWorkplaceTypesRole,
    skillAssessments: user ? isSkillsAssessmentsEnabled.allowedUsers.includes(user?._id) : false,
  };

  const hiringSection = {
    availability: true,
    bans: true,
    interviews: true,
    scraping: isPlacementScrapingConfigEnabled,
  };

  return {
    accountsSection: hasAnyTrueValue(accountsSection) ? accountsSection : undefined,
    shiftsSection: hasAnyTrueValue(shiftsSection) ? shiftsSection : undefined,
    invoiceSection: hasAnyTrueValue(invoiceSection) ? invoiceSection : undefined,
    payrollSection: hasAnyTrueValue(payrollSection) ? payrollSection : undefined,
    adminToolsSection: hasAnyTrueValue(adminToolsSection) ? adminToolsSection : undefined,
    staffingSection: hasAnyTrueValue(staffingSection) ? staffingSection : undefined,
    hiringSection,
    routeAuth,
  };
}
/* eslint-enable complexity */

export const [AdminMenuPrivilegesProvider, useAdminMenuPrivilegesProviderContext] =
  constate(useGetAdminMenuPrivileges);
